/**
 * 広告画像エディタ専用 画像選択モーダル
 *
 * - 過去アップロード画像を Storage `marketing/ad-imgs/` からグリッド表示
 * - 新規アップロード（D&D + ボタン）
 * - 選択した画像に「画像処理」を適用（Saturation/コントラスト/ガンマ/ホワイトバランス）
 * - Banana API で背景透明化
 *
 * onSelect で最終的な URL を返す。
 */
import { httpsCallable } from 'firebase/functions';
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref as storageRef,
  type StorageReference,
  uploadBytes,
} from 'firebase/storage';
import { Eraser, Loader2, RotateCcw, Sliders, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { functions, storage } from '@/lib/firebase';

import {
  applyAdjustments,
  DEFAULT_ADJUSTMENTS,
  loadImage,
  type ImageAdjustments,
} from '../utils/imageProcessing';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string) => void;
  /** 「過去画像」グリッドのフォルダパス（デフォルト marketing/ad-imgs） */
  storagePath?: string;
}

interface GalleryItem {
  url: string;
  name: string;
  fullPath: string;
}

const STORAGE_PATH = 'marketing/ad-imgs';
const MAX_BYTES = 10 * 1024 * 1024;
/** 一度に解決（getDownloadURL）して表示する件数。スクロールで順次追加。 */
const PAGE_SIZE = 30;

function safeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

/**
 * フォルダ内の全ファイル参照を「新しい順」で返す。
 * Storage の listAll は名前昇順で返し、ファイル名は `Date.now()-...` のため
 * reverse すればアップロード新着順になる。URL 解決はここでは行わない（遅延ロード）。
 */
async function listGalleryRefs(path: string): Promise<StorageReference[]> {
  const ref = storageRef(storage, path);
  const result = await listAll(ref);
  return [...result.items].reverse();
}

/** 参照配列を解決して GalleryItem（url 付き）に変換 */
async function resolveItems(refs: StorageReference[]): Promise<GalleryItem[]> {
  return Promise.all(
    refs.map(async (it) => {
      const url = await getDownloadURL(it);
      return { url, name: it.name, fullPath: it.fullPath };
    }),
  );
}

async function uploadFile(file: File): Promise<string> {
  if (file.size > MAX_BYTES) throw new Error(`ファイルサイズが大きすぎます（最大 10MB）`);
  if (!file.type.startsWith('image/')) throw new Error('画像ファイルを選択してください');
  const path = `${STORAGE_PATH}/${Date.now()}-${safeName(file.name)}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type });
  return getDownloadURL(ref);
}

export function AdImagePickerDialog({ open, onOpenChange, onSelect, storagePath = STORAGE_PATH }: Props) {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [adjustments, setAdjustments] = useState<ImageAdjustments>(DEFAULT_ADJUSTMENTS);
  const [showProcessor, setShowProcessor] = useState(false);
  const [processedPreviewUrl, setProcessedPreviewUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // 未解決（URL 未取得）の参照キュー。スクロールで順次解決する。
  const queueRef = useRef<StorageReference[]>([]);
  const loadingMoreRef = useRef(false);

  // 次ページ分の URL を解決して items に追加
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    const batch = queueRef.current.slice(0, PAGE_SIZE);
    if (batch.length === 0) {
      setHasMore(false);
      return;
    }
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const resolved = await resolveItems(batch);
      queueRef.current = queueRef.current.slice(batch.length);
      setItems((prev) => [...prev, ...resolved]);
      setHasMore(queueRef.current.length > 0);
    } catch (e) {
      toast.error(`画像取得失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  // 初回ロード（最新ページのみ解決、残りはキューに保持）
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setHasMore(false);
    queueRef.current = [];
    listGalleryRefs(storagePath)
      .then(async (refs) => {
        if (cancelled) return;
        const first = refs.slice(0, PAGE_SIZE);
        queueRef.current = refs.slice(first.length);
        const resolved = await resolveItems(first);
        if (cancelled) return;
        setItems(resolved);
        setHasMore(queueRef.current.length > 0);
      })
      .catch((e) => toast.error(`画像取得失敗: ${e.message}`))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, storagePath]);

  // スクロール末尾付近で次ページをロード
  const handleGalleryScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (
        hasMore &&
        !loadingMoreRef.current &&
        el.scrollHeight - el.scrollTop - el.clientHeight < 240
      ) {
        void loadMore();
      }
    },
    [hasMore, loadMore],
  );

  // ダイアログを閉じたら state リセット
  useEffect(() => {
    if (!open) {
      setSelectedUrl(null);
      setProcessedPreviewUrl(null);
      setAdjustments(DEFAULT_ADJUSTMENTS);
      setShowProcessor(false);
    }
  }, [open]);

  const currentImageUrl = processedPreviewUrl ?? selectedUrl;

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      const url = await uploadFile(files[0]);
      const newItem: GalleryItem = { url, name: files[0].name, fullPath: '' };
      setItems((prev) => [newItem, ...prev]);
      setSelectedUrl(url);
      setProcessedPreviewUrl(null);
      toast.success('アップロードしました');
    } catch (e) {
      toast.error(`アップロード失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleApplyAdjustments = async () => {
    if (!selectedUrl) return;
    setProcessing(true);
    try {
      const img = await loadImage(selectedUrl);
      const blob = await applyAdjustments(img, adjustments);
      const file = new File([blob], `processed-${Date.now()}.png`, { type: 'image/png' });
      const url = await uploadFile(file);
      setProcessedPreviewUrl(url);
      // ギャラリーにも追加
      setItems((prev) => [{ url, name: file.name, fullPath: '' }, ...prev]);
      toast.success('画像処理を適用しました');
    } catch (e) {
      toast.error(`処理失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteImage = async (item: GalleryItem) => {
    if (!confirm(`「${item.name}」を削除します。よろしいですか？\n（参照中の広告画像は表示が壊れる可能性があります）`)) return;
    try {
      if (item.fullPath) {
        await deleteObject(storageRef(storage, item.fullPath));
        queueRef.current = queueRef.current.filter((r) => r.fullPath !== item.fullPath);
      }
      setItems((prev) => prev.filter((x) => x.url !== item.url));
      if (selectedUrl === item.url) setSelectedUrl(null);
      if (processedPreviewUrl === item.url) setProcessedPreviewUrl(null);
      toast.success('削除しました');
    } catch (e) {
      toast.error(`削除失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleRemoveBackground = async () => {
    const target = currentImageUrl;
    if (!target) return;
    setRemovingBg(true);
    try {
      const fn = httpsCallable<{ imageUrl: string }, { url: string; storagePath: string }>(
        functions,
        'removeImageBackground',
        { timeout: 120_000 },
      );
      const res = await fn({ imageUrl: target });
      const url = res.data.url;
      setProcessedPreviewUrl(url);
      setItems((prev) => [{ url, name: 'bg-removed.png', fullPath: '' }, ...prev]);
      toast.success('背景を除去しました');
    } catch (e) {
      toast.error(`背景除去失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRemovingBg(false);
    }
  };

  const handleConfirm = () => {
    const url = currentImageUrl;
    if (!url) {
      toast.error('画像を選択してください');
      return;
    }
    onSelect(url);
    onOpenChange(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>画像を選択</DialogTitle>
          <DialogDescription>
            過去アップロードから選択、または新しい画像をアップロード。画像処理ボタンで色調整・背景除去ができます。
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 grid-rows-1 gap-4 h-[60vh] overflow-hidden">
          {/* 左: ギャラリー + アップロード */}
          <div className="flex flex-col gap-2 overflow-hidden min-h-0">
            <div
              onDrop={onDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              className={`border-2 border-dashed rounded-sm p-3 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                  : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="size-5 inline mr-2 text-[var(--text-muted)]" />
              <span className="text-xs text-[var(--text-muted)]">
                クリック / ドラッグ&ドロップで画像追加（最大 10MB）
              </span>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
              />
            </div>

            <div
              onScroll={handleGalleryScroll}
              className="flex-1 min-h-0 overflow-y-auto border border-[var(--border)] rounded-sm p-2"
            >
              {loading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : items.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] text-center py-8">
                  まだ画像がありません。
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-1">
                  {items.map((it) => (
                    <div
                      key={it.url}
                      className="group relative aspect-square overflow-hidden rounded-sm"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUrl(it.url);
                          setProcessedPreviewUrl(null);
                          setAdjustments(DEFAULT_ADJUSTMENTS);
                        }}
                        className={`w-full h-full border-2 rounded-sm transition ${
                          selectedUrl === it.url
                            ? 'border-[var(--accent)]'
                            : 'border-transparent hover:border-[var(--border)]'
                        }`}
                      >
                        <img
                          src={it.url}
                          alt={it.name}
                          className="w-full h-full object-cover"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(it);
                        }}
                        className="absolute top-1 right-1 p-1 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-[var(--accent-red)] transition-opacity"
                        title="この画像を削除"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </div>
                  ))}
                  </div>
                  {loadingMore && (
                    <div className="flex justify-center py-3">
                      <Spinner />
                    </div>
                  )}
                  {!hasMore && items.length > PAGE_SIZE && (
                    <p className="text-[10px] text-[var(--text-muted)] text-center py-2">
                      すべて読み込みました（{items.length} 件）
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* 右: プレビュー + 画像処理 */}
          <div className="flex flex-col gap-2 overflow-y-auto min-h-0">
            {currentImageUrl ? (
              <>
                <div className="aspect-square bg-[var(--bg-root)] rounded-sm border border-[var(--border)] overflow-hidden flex items-center justify-center">
                  <img
                    src={currentImageUrl}
                    alt="preview"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowProcessor((s) => !s)}
                  >
                    <Sliders className="size-3 mr-1" />
                    {showProcessor ? '画像処理を閉じる' : '画像処理'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleRemoveBackground}
                    disabled={removingBg}
                  >
                    {removingBg ? (
                      <Loader2 className="size-3 mr-1 animate-spin" />
                    ) : (
                      <Eraser className="size-3 mr-1" />
                    )}
                    AI 背景除去
                  </Button>
                </div>
                {showProcessor && (
                  <ImageAdjustmentPanel
                    adjustments={adjustments}
                    onChange={setAdjustments}
                    onApply={handleApplyAdjustments}
                    onReset={() => setAdjustments(DEFAULT_ADJUSTMENTS)}
                    processing={processing}
                  />
                )}
              </>
            ) : (
              <div className="aspect-square bg-[var(--bg-root)] rounded-sm border border-[var(--border)] flex items-center justify-center text-xs text-[var(--text-muted)]">
                左から画像を選択してください
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!currentImageUrl}>
            この画像を使う
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImageAdjustmentPanel({
  adjustments,
  onChange,
  onApply,
  onReset,
  processing,
}: {
  adjustments: ImageAdjustments;
  onChange: (next: ImageAdjustments) => void;
  onApply: () => void;
  onReset: () => void;
  processing: boolean;
}) {
  const setField = (k: keyof ImageAdjustments, v: number) =>
    onChange({ ...adjustments, [k]: v });
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-elevated)] space-y-3">
      <SliderRow
        label="彩度"
        min={-100}
        max={100}
        step={1}
        value={adjustments.saturation}
        onChange={(v) => setField('saturation', v)}
      />
      <SliderRow
        label="コントラスト"
        min={-100}
        max={100}
        step={1}
        value={adjustments.contrast}
        onChange={(v) => setField('contrast', v)}
      />
      <SliderRow
        label="ガンマ"
        min={0.2}
        max={3}
        step={0.05}
        value={adjustments.gamma}
        onChange={(v) => setField('gamma', v)}
      />
      <SliderRow
        label="ホワイトバランス"
        min={-100}
        max={100}
        step={1}
        value={adjustments.whiteBalance}
        onChange={(v) => setField('whiteBalance', v)}
        labelMin="青"
        labelMax="赤"
      />
      <div className="flex gap-2 pt-1">
        <Button type="button" variant="secondary" size="sm" onClick={onApply} disabled={processing}>
          {processing ? <Loader2 className="size-3 mr-1 animate-spin" /> : null}
          適用
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onReset}>
          <RotateCcw className="size-3 mr-1" />
          リセット
        </Button>
      </div>
    </div>
  );
}

function SliderRow({
  label,
  min,
  max,
  step,
  value,
  onChange,
  labelMin,
  labelMax,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  labelMin?: string;
  labelMax?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] text-[var(--text-secondary)]">{label}</label>
        <Input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="!w-20 !py-1 !text-[11px] text-right"
        />
      </div>
      <div className="flex items-center gap-2">
        {labelMin && <span className="text-[10px] text-[var(--text-muted)] w-3">{labelMin}</span>}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1"
        />
        {labelMax && <span className="text-[10px] text-[var(--text-muted)] w-3">{labelMax}</span>}
      </div>
    </div>
  );
}

